import * as React from 'react'

import { Box, Section, Spinner, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import employees from 'modules/employees/store/collections/employees'
import ConfirmationModal from 'modules/job_catalog/components/ConfirmationModal'
import useRemoveRoleSupervisor from 'modules/job_catalog/hooks/useRemoveRoleSupervisor'
import { useRoleSupervisors } from 'modules/job_catalog/hooks/useRoleSupervisors'

import AddRoleSupervisorModal from '../AddRoleSupervisorModal'
import RoleSupervisorsListItem from '../RoleSupervisorsListItem'

interface Props {
  roleId: number
}

const RoleSupervisorsForm: React.FC<Props> = ({ roleId }) => {
  const [supervisors, isFetching, refetch] = useRoleSupervisors(roleId)
  const removeRoleSupervisor = useRemoveRoleSupervisor()
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [nextRemoveSupervisorId, setNextRemoveSupervisorId] = React.useState<number>()
  const unassignedEmployees = useMemo(
    () => employees.active.filter((x) => !supervisors?.find((y) => y.id === x.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supervisors?.length]
  )

  const handleRemoveSupervisor = async () => {
    if (!nextRemoveSupervisorId) return

    const result = await removeRoleSupervisor(roleId, nextRemoveSupervisorId)
    const { success } = handleResult(result, () => undefined)

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.supervisor_removed_successfully'),
      })
      setNextRemoveSupervisorId(undefined)
      refetch()
      track(trackingEvents.jobCatalog.removeRoleSupervisorSubmit)
    }
  }

  const items = supervisors?.map((x, i) => (
    <RoleSupervisorsListItem
      key={x.id}
      label={x.fullName}
      onDelete={() => setNextRemoveSupervisorId(x.id)}
      disabled={supervisors.length === 1}
      bordered={i + 1 < supervisors.length}
    />
  ))

  const handleOpenModal = () => {
    setIsModalOpen(true)
    track(trackingEvents.jobCatalog.addRoleSupervisorStart)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    refetch()
  }

  return (
    <Section.List
      illustration="group"
      title={i18n.t('job_catalog.role_supervisors')}
      description={i18n.t('job_catalog.role_supervisors_description')}
      action={{
        label: i18n.t('job_catalog.add_role_supervisor'),
        onClick: handleOpenModal,
        disabled: !unassignedEmployees.length,
      }}
    >
      {!supervisors?.length && !isFetching && (
        <Box justifyContent="center" alignItems="center" padding="s24" flexGrow>
          <Text.Normal color="grey600" weight="medium" size="200">
            {i18n.t('job_catalog.no_supervisors_assigned')}
          </Text.Normal>
        </Box>
      )}
      {isFetching && (
        <Box alignItems="center" paddingY="s24">
          <Spinner />
        </Box>
      )}
      {!!supervisors?.length && !isFetching && items}
      {isModalOpen && (
        <AddRoleSupervisorModal
          roleId={roleId}
          employees={unassignedEmployees}
          onClose={handleCloseModal}
        />
      )}
      {nextRemoveSupervisorId && (
        <ConfirmationModal
          title={i18n.t('job_catalog.remove_role_supervisor_title')}
          description={i18n.t('job_catalog.remove_role_supervisor_description')}
          onConfirm={() => handleRemoveSupervisor()}
          onCancel={() => setNextRemoveSupervisorId(undefined)}
        />
      )}
    </Section.List>
  )
}

export default observer(RoleSupervisorsForm)
